import React, { useEffect, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import { Title } from '@fs/zion-head'
import { Body1, Button, DataPreview, HeaderBlock, LayoutBand } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { AncestorCard, SkeletonAncestorCard } from '@fs/tree-ancestor-card'
import AdvancedSearchCard from './AdvancedSearchCard'
import Search from './Search'
import SearchError from './SearchError'
import { doSearch, getCachedSearchParams, setCachedSearchParams, getTreeFindUrl } from '../../services/search-service'
import sendAnalytics from '../../services/analytics-helper'
import type { AncestorSearch, SearchParams } from '../../types/data-types'
import NoResultsSvg from './NoResults.svg'

const noResultsCss = css`
  margin: 50px auto;
  text-align: center;

  & img {
    margin-bottom: 20px;
  }
`

const subtextCss = css`
  margin: 0 auto;
  max-width: 500px;
  text-align: center;
`

const cardCss = css`
  &:hover {
    cursor: pointer;
  }
`

const resultsCss = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  list-style: none;
  margin: 24px 0 48px 0;
  padding: 0;
  & > li {
    margin: 6px;
    padding: 0;
  }
`
const linkCss = css`
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`

const skeletonCards = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}]

export default function SearchPage(): JSX.Element {
  const [t] = useTranslation()
  const [treeFindUrl, setTreeFindUrl] = useState('')
  const initialParams: SearchParams = getCachedSearchParams()
  const searchOnLoad = useRef(false)
  const initialSearch: AncestorSearch = { params: initialParams }
  const [search, setSearch] = useState(initialSearch)
  const [loading, setLoading] = useState(false)
  const [searchError, setSearchError] = useState(false)
  usePageView({ page_detail: 'Search' })
  getTreeFindUrl(search?.params).then((url) => {
    setTreeFindUrl(url)
  })

  function onSubmit(params: SearchParams, startIndex?: number): void {
    setCachedSearchParams(params)
    setLoading(true)
    doSearch(params, startIndex).then((results) => {
      if (results.error) {
        setSearchError(true)
      } else {
        setSearchError(false)
      }
      setLoading(false)
      setSearch(results)
    })
  }

  useEffect(() => {
    let cancelled = false
    if (!searchOnLoad.current && !cancelled && !loading && !search?.records && initialParams?.self?.surname?.value) {
      searchOnLoad.current = true
      onSubmit(initialParams, 0)
    }

    return () => {
      cancelled = true
    }
  }, [initialParams, loading, search, search.records])

  return (
    <>
      <Title>{t('ancestors.surname-index.meta-data.title')}</Title>
      <LayoutBand maxWidth="md">
        <Search
          onSearchPage
          params={initialParams}
          onSubmit={onSubmit}
          header={t('ancestors.search-for-ancestor.header')}
          showMore
          analytics={{ link_page: 'Search' }}
        />

        {searchError && <SearchError />}
      </LayoutBand>

      <LayoutBand maxWidth="lg">
        <DataPreview.Provider loading={loading}>
          <DataPreview.Loading>
            <ul css={resultsCss}>
              {skeletonCards &&
                skeletonCards.map((card, index) => {
                  const key = `skeleton-${index}`
                  return (
                    <li key={key}>
                      <SkeletonAncestorCard hidePid />
                    </li>
                  )
                })}
            </ul>
          </DataPreview.Loading>
          <DataPreview.Ready>
            {search?.records?.length !== 0 && (
              <>
                <ul css={resultsCss}>
                  {search?.records &&
                    search.records.map((ancestorData) => {
                      return (
                        <li
                          css={cardCss}
                          key={ancestorData.person.identifier}
                          data-testid={`search-result-${ancestorData.person.identifier}`}
                          aria-label={t('ancestors.person.label')}
                        >
                          <a
                            data-testid="search-link"
                            href={ancestorData.person.url}
                            onClick={() => {
                              sendAnalytics({ link_page: 'Search', link_name: 'Person' })
                            }}
                            css={linkCss}
                          >
                            <AncestorCard ancestorData={ancestorData} hidePid />
                          </a>
                        </li>
                      )
                    })}

                  {search?.records && (
                    <li css={cardCss} key="advanced-search-help" aria-label={t('ancestors.advanced-search-card.label')}>
                      <AdvancedSearchCard treeFindUrl={treeFindUrl} />
                    </li>
                  )}
                </ul>
              </>
            )}
            {search?.records?.length === 0 && (
              <div css={noResultsCss}>
                <img src={NoResultsSvg} alt={t('ancestors.search-not-found.header')} />
                <div>
                  <HeaderBlock heading={t('ancestors.search-not-found.header')} size="md" />
                  <div css={subtextCss}>
                    <Body1>{t('ancestors.seach-not-found.text')}</Body1>
                  </div>
                  <Button
                    data-testid="family-tree-search-button"
                    onClick={async () => {
                      sendAnalytics({ link_page: 'Search', link_name: 'Family Tree Search' })
                      if (window?.location?.href) {
                        window.location.href = treeFindUrl
                      }
                    }}
                  >
                    {t('ancestors.family-tree-search.action')}
                  </Button>
                </div>
              </div>
            )}
          </DataPreview.Ready>
        </DataPreview.Provider>
      </LayoutBand>
    </>
  )
}
